/**
 * API Client - Axios instance with JWT token attachment
 * 
 * Configures Axios with:
 * - Base URL from environment variable
 * - Request interceptor to attach JWT token
 * - Response interceptor to handle 401 errors with token refresh
 * 
 * Requirements: 12.6, 2.6, 2.7
 */

import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';

/**
 * Token provider interface
 * 
 * Allows the API client to get the current access token
 * without directly depending on the auth store.
 */
export interface TokenProvider {
  getAccessToken: () => string | null;
  refreshToken: () => Promise<string | null>;
  clearAuth: () => void;
}

// Token provider instance - will be set by auth module
let tokenProvider: TokenProvider | null = null;

/**
 * Set the token provider for the API client
 * 
 * This should be called during app initialization to connect
 * the API client with the auth store.
 */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/**
 * Get the token provider
 * 
 * Used for testing purposes.
 */
export function getTokenProvider(): TokenProvider | null {
  return tokenProvider;
}

/**
 * Create the Axios instance with base configuration
 */
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - Attach JWT Token
 * 
 * For any API request made by an authenticated user,
 * the Authorization header SHALL contain a valid JWT token from Cognito.
 * 
 * Property 7: JWT Token Attachment
 * Validates: Requirements 2.6
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Get access token from token provider
    const accessToken = tokenProvider?.getAccessToken();
    
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle 401 errors with token refresh
 * 
 * When receiving a 401 Unauthorized response:
 * 1. Try to refresh the token
 * 2. If refresh succeeds, retry the original request
 * 3. If refresh fails, clear auth and redirect to login
 * 
 * Requirements: 2.7
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Network Error handling (defense in depth)
    // When API Gateway returns 401 without CORS headers, browser blocks the response
    // and Axios receives a Network Error (error.response is undefined).
    // Detect this case and attempt token refresh as a fallback.
    const isNetworkError = !error.response && error.message?.includes('Network Error');
    const hasToken = tokenProvider?.getAccessToken() != null;

    if (isNetworkError && hasToken && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (token && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        const newToken = await tokenProvider?.refreshToken();

        if (newToken) {
          // Refresh succeeded - retry original request
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          processQueue(null, newToken);
          return api(originalRequest);
        } else {
          // Refresh failed - clear auth and redirect
          processQueue(new Error('Token refresh failed'), null);
          if (tokenProvider) {
            tokenProvider.clearAuth();
          }
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } catch (refreshError) {
        // Refresh threw error - clear auth and redirect
        processQueue(refreshError as Error, null);
        if (tokenProvider) {
          tokenProvider.clearAuth();
        }
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } finally {
        isRefreshing = false;
      }

      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (token && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Try to refresh the token
        const newToken = await tokenProvider?.refreshToken();
        
        if (newToken) {
          // Refresh succeeded - retry original request
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          processQueue(null, newToken);
          return api(originalRequest);
        } else {
          // Refresh failed - clear auth and redirect
          processQueue(new Error('Token refresh failed'), null);
          if (tokenProvider) {
            tokenProvider.clearAuth();
          }
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } catch (refreshError) {
        // Refresh threw error - clear auth and redirect
        processQueue(refreshError as Error, null);
        if (tokenProvider) {
          tokenProvider.clearAuth();
        }
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

/**
 * Export the axios instance for testing
 */
export { api };
